import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, Linking } from 'react-native'
import { Button, Text, Spinner, Datepicker, Icon } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import { saveNewManufacture, deleteManufacture } from '../../global/Firebase/firebase'
import ProductList from '../../components/ProductList'
import HeaderIconButton from '../../components/HeaderIconButton'
import * as Sentry from "@sentry/react-native"
import { IngredientsView } from '../../components/IngredientsView'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { localeDateServiceAtom, productCategoriesDictAtom, productsDictAtom, storesDictAtom, userRoleAtom, userStoreIdAtom } from '../../global/AtomsProvider'
import { getCompositeProductsByCategoryList } from '../../utils/productListHelpers'
import { getDailyStatID } from '../../utils/timezoneFunctions'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import { newFuse } from '../../utils/fuseHelper'
import { calculateTotalPurchasePriceFromIngredients } from '../../utils/ingredientCalculations'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
import { saveFailureText } from '../../global/Firebase/writeOutcome'

export default function ManufactureDetailScreen( { navigation, route } ) {
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const { detail, itemIndex, sectionIndex, addItemToData, editItemInData, removeItemFromData, preselectedStore } = route.params
  const [store, setStore] = useState(detail ? detail.store : (preselectedStore || context.user.store?.id))
  const productsDict = useAtomValue(productsDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const productCategoriesDict = useAtomValue(productCategoriesDictAtom)
  const [compositeProductsByCategoryList, setCompositeProductsByCategoryList] = useState(getCompositeProductsByCategoryList(productsDict, productCategoriesDict, true))
  const [fuse, setFuse] = useState(newFuse(compositeProductsByCategoryList))
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const [product, setProduct] = useState(detail ? detail.product : null)
  const [ingredients, setIngredients] = useState(detail ? detail.ingredients : [])
  const [ingredientsEdited, setIngredientsEdited] = useState(detail ? detail.ingredientsEdited : false)
  const [quantity, setQuantity] = useState(detail ? detail.quantity.toString() : '1')
  const [individualPurchasePrice, setIndividualPurchasePrice] = useState(
    detail ? (detail.totalSum / detail.quantity) : 0
  )
  const [batchNumber, setBatchNumber] = useState(detail?.batchNumber || '')
  const [totalSum, setTotalSum] = useState(detail ? detail.totalSum : 0)
  const [manufactureDate, setManufactureDate] = useState(new Date())
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const _datePicker = React.createRef()

  useEffect(() => {
    let newCompositeProductsByCategoryList = getCompositeProductsByCategoryList(productsDict, productCategoriesDict, true)
    setCompositeProductsByCategoryList(newCompositeProductsByCategoryList)
    setFuse(newFuse(newCompositeProductsByCategoryList))
  }, [store])

  // Log manufacture detail for E2E testing
  useEffect(() => {
    if (detail?.id) {
      console.log('[MANUFACTURE_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        product: {
          id: detail.product?.id,
          name: detail.product?.name,
          purchasePrice: detail.product?.purchasePrice,
          isComposite: detail.product?.isComposite,
        },
        quantity: detail.quantity,
        ingredients: (detail.ingredients || []).map(ing => ({
          id: ing.id,
          name: ing.name || ing.text,
          quantity: ing.quantity,
          purchasePrice: ing.purchasePrice,
        })),
        ingredientsEdited: detail.ingredientsEdited,
        totalSum: detail.totalSum,
        store: detail.store,
        cashierUID: detail.cashierUID,
        organizationId: context.user?.organizationId,
      })));
    }
  }, [detail])

  useEffect(() => {
    if (userRole?.permissions?.deleteManufacture || context.user.role === 'admin') {
      navigation.setOptions({
        headerRight: () => (
          detail != null && <HeaderIconButton
            onPress={async () => {
              if (!isLoading) {
                try {
                  setIsLoading(true)
                  await deleteManufacture(db, detail.ref, detail)
                  removeItemFromData(sectionIndex, itemIndex)
                  setIsLoading(false)
                  navigation.goBack()
                } catch(error) {
                  console.log(error)
                  setSaveError(t('error'))
                  Sentry.captureException(error, { tags: { books_write_error: 'manufacture-delete' } })
                }
              }
            }}
            iconName={isLoading ? 'loader-outline' : 'trash-outline'}
            testID='detail-delete-button'
          />
        ),
      })
    }
  }, [isLoading])

  useEffect(() => {
    // Only recalculate totalSum for new manufactures, not when viewing existing ones
    // This prevents overwriting the correct backend value with a recalculated one
    if (detail == null) {
      const updatedTotalSum = (individualPurchasePrice || 0) * quantity
      setTotalSum(updatedTotalSum)
    }
  }, [individualPurchasePrice, quantity])

  const chooseProduct = (item) => {
    if (item.isComposite) {
      let newProductInfo = {...item}
      let newIngredients = []
      for (let p of item.ingredients) {
        let newI = {...productsDict[p.id], quantity: p.quantity}
        newIngredients.push(newI)
      }
      const newPurchasePrice = calculateTotalPurchasePriceFromIngredients(newIngredients)
      newProductInfo.ingredients = newIngredients
      newProductInfo.purchasePrice = newPurchasePrice
      setIngredients(newIngredients)
      setProduct(newProductInfo)
      setIndividualPurchasePrice(newPurchasePrice)
    } else {
      setProduct(item)
    }
  }

  async function save() {
    if (detail == null) {
      setIsLoading(true)
      if (parseFloat(quantity) > 0) {
        setSaveError('')
        const info = {
          product: product,
          quantity: parseFloat(quantity),
          ingredientsEdited,
          ingredients,
          totalSum,
          date: manufactureDate,
          // ATL-1684 / ATL-1701: bucket by the manufacture's OWN store business day (autoclose-aware),
          // not the cashier's default store. `store` is the id of the store the manufacture is saved to.
          dailyStatID: getDailyStatID(store, manufactureDate),
          batchNumber,
          cashierUID: context.user.uid,
          store,
          organizationId: context.user.organizationId
        }
        try {
          let ref = await saveNewManufacture(db, info)
          addItemToData({...info, ref, id: ref.id}, info.date, info.store)
          console.log('[MANUFACTURE_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
            id: ref.id,
            supabaseId: ref.supabaseId,
            product: {
              id: info.product?.id,
              name: info.product?.name,
              purchasePrice: info.product?.purchasePrice,
              isComposite: info.product?.isComposite,
            },
            quantity: info.quantity,
            ingredients: (info.ingredients || []).map(ing => ({
              id: ing.id,
              name: ing.name || ing.text,
              quantity: ing.quantity,
              purchasePrice: ing.purchasePrice,
            })),
            ingredientsEdited: info.ingredientsEdited,
            totalSum: info.totalSum,
            store: info.store,
            cashierUID: info.cashierUID,
            organizationId: context.user?.organizationId,
          })));
          setSuccessMessageVisible(true)
          setIsLoading(false)
        } catch (error) {
          Sentry.captureException(error, { tags: { books_write_error: 'manufacture-create' } })
          console.log(error)
          if (error.message === 'INGREDIENT_NOT_FOUND') {
            setSaveError(t('ingredientNotFoundError', { 
              name: error.ingredientName,
              product: error.productName 
            }))
          } else {
            setSaveError(saveFailureText(error, t, t('error')))
          }
          setIsLoading(false)
        }
      } else {
        setSaveError(t('notAllFieldsFilled'))
        setIsLoading(false)
      }
    }
  }

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
     { product == null ? (
      <ProductList showStoreFilter={true} products={compositeProductsByCategoryList} customFuse={fuse}
        links={[{
          action: () => {Linking.openURL(t('manufactures.mainHintLink'))},
          text: t('manufactures.mainHintAction'),
          icon: 'question-mark-circle-outline'
        }]}
        onItemPress={(item, index, store)=>{
          chooseProduct(item)
          setStore(store)
        }} 
        preselectedStore={store}
      />
     ) : (
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        {detail && <Text appearance='hint' style={{textAlign: 'center', marginVertical: 10}}>
          {detail.id}
        </Text>}
        { !detail && context.user.role === 'admin' && 
          <Button style={{alignSelf: 'flex-end', marginRight: -20}} size='small' appearance='ghost' onPress={() => {
              Linking.openURL(t('manufactures.mainHintLink'))
            }} accessoryLeft={(props) => (<Icon name='question-mark-circle-outline' {...props} 
            style={{...props.style, marginRight: 0}} size='small' />)}
          >
            {t('manufactures.mainHintAction')}
          </Button>
        }
        <Text category='label' style={styles.label}>
          {t('product') + ' - ' + product.name}
        </Text>
        { Object.values(storesDict).length > 1 && (userRole?.permissions?.['viewOtherStores'] || context.user.role === 'admin') && (
          <>
            <Text category='label' style={styles.label}>
              {t('store')}
            </Text>
            <PlatformDependantSelect
              data={Object.values(storesDict)}
              selectedOption={storesDict?.[store]}
              disabled={detail != null}
              onSelect={(itemValue, itemIndex) => {
                setStore(itemValue.id)
              }}
            />
          </>
        )}
        <Text category='label' style={styles.label}>
          {t('manufactures.manufacturedDate')}
        </Text>
        <Datepicker
          placement='bottom'
          date={manufactureDate}
          dateService={localeDateService}
          onSelect={(date) => {
            let currentDate = new Date()
            date.setHours(currentDate.getHours(), currentDate.getMinutes())
            setManufactureDate(date)
            _datePicker.current.blur()
          }}
          min={new Date('2020-12-01')}
          max={new Date('2029-12-31')}
          ref={_datePicker}
          disabled={detail != null}
        />
        <Text category='label' style={styles.label}>
          {t('manufactures.quantity')}
        </Text>
        <StyledTextInput 
          value={quantity}
          onChangeText={text => setQuantity(text.replace(/,/g, '.'))}
          keyboardType='decimal-pad'
          placeholder='0'
          // style={styles.quantityField}
          returnKeyType='done'
          disabled={detail != null}
          selectTextOnFocus={true}
        />
        { (detail == null || !!detail.batchNumber) && (
          <>
            <Text category='label' style={styles.label}>
              {t('manufactures.batchNumber')}
            </Text>
            <StyledTextInput
              value={batchNumber}
              onChangeText={setBatchNumber}
              placeholder={t('manufactures.batchNumberPlaceholder')}
              maxLength={100}
              returnKeyType='done'
              disabled={detail != null}
            />
          </>
        )}
        <IngredientsView parentProduct={product} editable={detail != null ? false : ingredientsEdited} 
          setEditable={setIngredientsEdited}
          parentIngredients={ingredients} setParentIngredients={setIngredients} 
          navigation={navigation}
          parentPurchasePrice={individualPurchasePrice} setParentPurchasePrice={setIndividualPurchasePrice} 
          showParentQuantity={true} parentQuantity={parseFloat(quantity)}
          hideEditButton={detail != null} style={{marginTop: detail != null ? 16 : 0}}
        />
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && (
          <Button onPress={save} style={styles.submitButton} disabled={detail != null}>
            {t('save')}
          </Button>
          )
        )}
        <View style={{height: 100}}></View>
      </ScrollView>
     )}
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
  },
  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
});